import { ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { colorTokens } from "../../../styles/designTokens";

export default function SidebarItem({
  item,
  role,
  activePath,
  typography,
  onItemSelect,
}) {
  const [open, setOpen] = useState(false);

  const hasChildren = item.subtitle && item.subtitle.length > 0;

  const childrenFiltered = hasChildren
    ? item.subtitle.filter((s) => !s.roles || s.roles.includes(role))
    : [];

  const isPathActive = (currentPath, targetPath) => {
    if (!targetPath) return false;
    if (targetPath === "/") return currentPath === "/";
    return (
      currentPath === targetPath || currentPath.startsWith(`${targetPath}/`)
    );
  };

  const isActive =
    (item.to && isPathActive(activePath, item.to)) ||
    childrenFiltered.some((s) => s.to && isPathActive(activePath, s.to));

  useEffect(() => {
    if (isActive && hasChildren) setOpen(true);
  }, [isActive, hasChildren]);

  const baseTypographyStyle = typography
    ? {
        fontFamily: typography.fontFamily,
        fontSize: typography.fontSize,
        fontWeight: typography.fontWeight,
      }
    : undefined;

  const activeTypographyStyle = typography
    ? {
        fontFamily: typography.fontFamily,
        fontSize: typography.fontSize,
        fontWeight: typography.activeFontWeight || typography.fontWeight,
      }
    : undefined;

  const actionSecondaryActive =
    colorTokens?.actionSecondaryActive || "#e4e4e4";
  const actionSecondaryBase2 =
    colorTokens?.actionSecondaryBase2 || "#f2f2f2";

  const baseClasses =
    "group relative flex items-center justify-between w-full rounded-lg py-2 pl-6 pr-2 text-slate-600 transition-colors";

  const handleHoverEnter = (event, active) => {
    if (active) return;
    event.currentTarget.style.backgroundColor = actionSecondaryBase2;
  };

  const handleHoverLeave = (event, active) => {
    if (active) {
      event.currentTarget.style.backgroundColor = actionSecondaryActive;
    } else {
      event.currentTarget.style.backgroundColor = "";
    }
  };

  return (
    <div className="w-full">
      {/* ITEM PRINCIPAL */}
      {item.to ? (
        <Link
          to={item.to}
          className={`${baseClasses} ${
            isActive ? "font-medium text-slate-900" : ""
          }`}
          style={
            isActive
              ? {
                  ...(activeTypographyStyle || {}),
                  backgroundColor: actionSecondaryActive,
                }
              : baseTypographyStyle
          }
          onClick={onItemSelect}
          onMouseEnter={(event) => handleHoverEnter(event, isActive)}
          onMouseLeave={(event) => handleHoverLeave(event, isActive)}
          >
            <span
              aria-hidden="true"
              className={`pointer-events-none absolute -left-[13px] top-1/2 h-3 w-3 -translate-y-1/2 transition-colors ${
                isActive
                  ? "text-blue-200"
                  : "text-slate-200 group-hover:text-slate-300"
              }`}
            >
              <svg viewBox="0 0 12 12" className="h-full w-full">
                <path
                  d="M0 0 C0 6 6 6 12 6"
                  fill="none"
                stroke="currentColor"
                strokeWidth="1"
                strokeLinecap="round"
              />
            </svg>
          </span>
          <span className="truncate">{item.title}</span>
        </Link>
      ) : (
        <button
          type="button"
          className={`${baseClasses} ${
            isActive ? "font-medium text-slate-900" : ""
          }`}
          onClick={() => setOpen((prev) => !prev)}
          style={
            isActive
              ? {
                  ...(activeTypographyStyle || {}),
                  backgroundColor: actionSecondaryActive,
                }
              : baseTypographyStyle
          }
          onMouseEnter={(event) => handleHoverEnter(event, isActive)}
          onMouseLeave={(event) => handleHoverLeave(event, isActive)}
        >
          <span
            aria-hidden="true"
            className={`pointer-events-none absolute -left-[13px] top-1/2 h-3 w-3 -translate-y-1/2 transition-colors ${
              isActive
                ? "text-blue-200"
                : "text-slate-200 group-hover:text-slate-300"
            }`}
          >
            <svg viewBox="0 0 12 12" className="h-full w-full">
              <path
                d="M0 0 C0 6 6 6 12 6"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
                strokeLinecap="round"
              />
            </svg>
          </span>
          <span className="truncate">{item.title}</span>
              {hasChildren && (
                <ChevronDown
                  className={`h-4 w-4 text-slate-400 transition-transform ${
                    open ? "rotate-180" : ""
                  }`}
                />
              )}
        </button>
      )}

      {/* SUBTITLES / SUBITEMS */}
      {hasChildren && (
        <div
          className={`ml-3 mt-1 border-l border-slate-200 pl-3 flex flex-col gap-0.5 transition-all ${
            open
              ? "max-h-[500px] opacity-100"
              : "max-h-0 opacity-0 overflow-hidden"
          }`}
        >
          {childrenFiltered.map((s) => {
            const activeSub = s.to && isPathActive(activePath, s.to);

            return (
              <Link
                key={s.to}
                to={s.to}
                className="group relative flex w-full items-center rounded-md py-1.5 pl-6 pr-2 transition-colors"
                style={
                  activeSub
                    ? {
                        ...(activeTypographyStyle || {}),
                        backgroundColor: actionSecondaryActive,
                      }
                    : baseTypographyStyle
                }
                onClick={onItemSelect}
                onMouseEnter={(event) => handleHoverEnter(event, activeSub)}
                onMouseLeave={(event) => handleHoverLeave(event, activeSub)}
              >
                <span
                  aria-hidden="true"
                  className={`
    pointer-events-none absolute -left-[13px] top-1/2
    h-3 w-3 -translate-y-1/2
    transition-colors
    ${activeSub ? "text-blue-200" : "text-slate-200 group-hover:text-slate-300"}
  `}
                >
                  <svg viewBox="0 0 12 12" className="h-full w-full">
                    <path
                      d="M0 0 C0 6 6 6 12 6"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>

                <span
                  className={`truncate ${
                    activeSub ? "text-slate-900 font-medium" : "text-slate-500"
                  }`}
                >
                  {s.subTitle}
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
